<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import type { CartItemType, ProductType } from "@/types";
import Vue3Slider from "vue3-slider";

type PropsType = {
  product: ProductType;
  cartItem: CartItemType;
  handlePosition: string;
  updateHandlePosition: () => void;
  totalItemsInCart: number;
  updateQuantity: (action: string) => void;
  ctaButton?: boolean;
  addToCart: () => void;
  buyNow: () => void;
  clearFields: () => void;
};

defineProps<PropsType>();

const {
  currency,
  getProductPrice,
  getProductDiscountValue,
  getPlanDurationText,
} = formatterUtils();
</script>
<template>
  <div class="product-page container">
    <div class="text-black uppercase text-base font-semibold self-end py-10">
      <NuxtLink to="/">Home</NuxtLink> / Product
    </div>
    <div v-if="product" class="content">
      <div class="product-detail">
        <PlanCard :product="product" :cta-button="false" />
      </div>
      <div class="add-to-cart">
        <section
          class="flex flex-col mt-2.5 text-base font-semibold text-neutral-800 max-md:mt-10 max-md:max-w-full items-center md:items-start"
        >
          <h2
            class="md:font-bold font-semibold max-md:max-w-full uppercase text-base text-center md:text-3xl"
          >
            {{ product.name }}
          </h2>
          <p
            v-if="getProductDiscountValue(product) > 0"
            class="mt-4 text-center md:text-left max-md:max-w-full font-light line-through text-font-primary"
          >
            {{ currency(getProductPrice(product, false)) }}
          </p>
          <p class="mt-2 leading-6 text-center md:text-left max-md:max-w-full">
            Price: {{ currency(getProductPrice(product)) }}
            <span class="font-normal">{{ getPlanDurationText(product) }}</span>
          </p>
          <div class="my-4 border-t-2 border-dashed-200 w-full"></div>
          <p class="text-primary leading-[150%] max-md:max-w-full">In Stock</p>
          <p class="mt-5 leading-[137.5%] text-neutral-800 max-md:max-w-full">
            Quantity
          </p>
          <div
            v-if="product.category?.some((cat: any) => cat.code === 'FLEXI')"
            class="flex flex-col mt-4 w-full max-w-xs gap-2"
          >
            <div class="relative w-full">
              <ClientOnly>
                <div class="slider-container">
                  <div class="flex items-center justify-between w-full gap-4">
                    <div class="flex-1">
                      <Vue3Slider
                        v-model="cartItem.quantity"
                        :min="1"
                        :max="12"
                        :step="1"
                        :tooltip="false"
                        @change="updateHandlePosition"
                      />
                      <span
                        class="quantity-label"
                        :style="{ left: handlePosition }"
                      >
                        {{ cartItem.quantity }}
                      </span>
                    </div>
                    <span class="text-sm font-semibold whitespace-nowrap">
                      {{
                        currency(cartItem.quantity * getProductPrice(product))
                      }}
                    </span>
                  </div>
                </div>
              </ClientOnly>
            </div>
          </div>
          <div class="flex mt-4" v-else>
            <button
              class="flex justify-center items-center p-3 z-10 border"
              @click="updateQuantity('remove')"
              aria-label="Decrease quantity"
            >
              <ChevronLeftIcon class="h-5 w-5" />
              <span class="sr-only">Decrease quantity</span>
            </button>
            <label class="sr-only" for="quantity">Quantity</label>
            <input
              id="quantity"
              v-model="cartItem.quantity"
              disabled
              type="number"
              class="text-center text-md font-semibold rounded w-12 focus:outline-none"
              aria-label="Product quantity"
            />
            <button
              class="flex justify-center items-center p-3 z-10 border"
              @click="updateQuantity('add')"
              aria-label="Increase quantity"
            >
              <ChevronRightIcon class="h-5 w-5" />
              <span class="sr-only">Increase quantity</span>
            </button>
          </div>

          <!-- Buttons -->
          <div
            class="flex gap-2.5 self-center md:self-start mt-4 text-sm leading-6 text-white"
          >
            <button
              class="justify-center px-4 py-1.5 bg-primary rounded transition-all delay-75"
              @click="addToCart()"
            >
              ADD TO CART
            </button>
            <button
              class="justify-center px-4 py-1.5 bg-primary rounded drop-shadow-xl transition-all delay-75"
              @click="buyNow()"
            >
              BUY NOW
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.product-page {
  @apply container flex flex-col bg-background;
  .content {
    @apply flex flex-row justify-center max-md:flex-col;

    .product-detail {
      @apply w-1/2 flex justify-center max-md:w-full max-md:mb-10;
    }
    .add-to-cart {
      @apply w-1/2 flex flex-col items-center max-md:w-full max-md:justify-center my-7;
      @screen md {
        @apply my-7;
      }
    }
  }
}

.slider-container {
  position: relative;
  padding-bottom: 24px;

  .quantity-label {
    position: absolute;
    bottom: 0;
    transform: translateX(-50%);
    font-size: 14px;
    color: rgb(var(--text));
  }
}

:deep(.vue3-slider) {
  width: 100%;
  --height: 6px;
  --color: rgb(var(--primary));
  --track-color: rgb(var(--secondary));
  --tooltip-color: #ffffff;
  --tooltip-text-color: #000000;
  --handle-scale: 1;
  --handle-color: rgb(var(--primary));

  .tooltip {
    transform: translate(0px);
    margin: calc(var(--height, 6px) + 12px);
    bottom: var(--tooltip-margin);
    top: unset;
    display: none;
  }

  .track {
    background-color: rgb(var(--secondary));
  }

  .track-filled {
    background-color: rgb(var(--primary));
  }

  .handle {
    width: 16px;
    height: 16px;
    background-color: rgb(var(--primary));
    border: none;
    box-shadow: none;
    transform: translate(-50%, -40%);
    border-radius: 8px;
  }
}
</style>
